/**
 * 认证服务
 * 封装所有与用户认证相关的 API 调用
 */
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

const throwApiError = async (response, fallbackMessage = '请求失败') => {
    let payload = {};
    try {
        payload = await response.json();
    } catch (_e) {
        payload = {};
    }
    const message = payload?.error || payload?.message || fallbackMessage;
    const error = new Error(message);
    error.response = { data: payload };
    throw error;
};

class AuthService {
    /**
     * 获取图形验证码
     */
    async getCaptcha() {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.captcha}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await throwApiError(response, '获取验证码失败');
        }

        return response.json();
    }

    /**
     * 发送登录用短信验证码
     * @param {Object} data - { phone, captcha_id, captcha_code }
     */
    async sendSmsForLogin(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.sendSmsForLogin}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '发送短信失败');
        }

        return response.json();
    }

    /**
     * 手机验证码登录/注册
     * @param {Object} data - { phone, code, nickname? }
     */
    async loginBySms(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.loginBySms}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '短信登录失败');
        }

        return response.json();
    }

    /**
     * 账号密码登录
     * @param {Object} data - { username, password }
     */
    async login(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.login}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '登录失败');
        }

        return response.json();
    }

    /**
     * 用户注册
     * @param {Object} data - { username, password, phone, code }
     */
    async register(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.register}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '注册失败');
        }

        return response.json();
    }

    /**
     * 发送注册用短信验证码
     * @param {Object} data - { phone, captcha_id, captcha_code }
     */
    async sendSms(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.sendSms}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '发送短信失败');
        }

        return response.json();
    }

    /**
     * 用户登出
     * @param {string} token - JWT token
     */
    async logout(token) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.auth.logout}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            await throwApiError(response, '退出失败');
        }

        return response.json();
    }

    /**
     * 创建微信扫码登录会话
     */
    async createWechatLoginSession() {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.wechat.loginSession}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await throwApiError(response, '创建微信会话失败');
        }

        return response.json();
    }

    /**
     * 检查微信扫码登录状态
     * @param {string} ticket - 会话ticket
     */
    async checkWechatLoginStatus(ticket) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.wechat.loginStatus}?ticket=${ticket}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await throwApiError(response, '查询微信状态失败');
        }

        return response.json();
    }

    /**
     * 微信登录绑定手机号
     * @param {Object} data - { scene_str, phone, code }
     */
    async bindPhoneForWechat(data) {
        const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.wechat.bindPhone}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            await throwApiError(response, '微信绑定失败');
        }

        return response.json();
    }
}

const authService = new AuthService();
export default authService;

